import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry, createCheckout } from "@/lib/api";

const inputCls =
  "w-full bg-cream border border-chocolate/15 px-4 py-3 text-sm text-chocolate placeholder:text-chocolate/35 focus:outline-none focus:border-gold transition-colors rounded-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/60 mb-2";

export default function ProductModal({ product, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await createCheckout(product.id);
      window.location.href = checkout_url;
    } catch {
      toast.error("Couldn't start secure checkout. Please try again or send an enquiry below.");
      setBuying(false);
    }
  };

  useEffect(() => {
    if (product) {
      setForm({ name: "", email: "", phone: "", message: `I'd like to purchase ${product.name} (${product.price}).` });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [product]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can reply.");
      return;
    }
    setSending(true);
    try {
      await submitEnquiry("product", { product: product.name, price: product.price, ...form });
      toast.success("Enquiry sent — we'll confirm availability within one working day.");
      onClose();
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="product-detail-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-espresso/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-cream w-full max-w-4xl max-h-[90vh] overflow-y-auto no-scrollbar border border-gold/40 grid md:grid-cols-2"
          >
            <button
              data-testid="product-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 w-9 h-9 bg-cream/90 border border-chocolate/15 flex items-center justify-center text-chocolate hover:bg-chocolate hover:text-cream transition-colors"
            >
              <X size={16} />
            </button>
            <div className="relative min-h-[280px] md:min-h-full">
              <img src={product.image} alt={product.name} className="img-warm absolute inset-0 w-full h-full object-cover" />
              <span className="absolute bottom-4 left-4 bg-cream/90 backdrop-blur px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-chocolate">
                {product.lead}
              </span>
            </div>
            <div className="p-8 md:p-10">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-golddark mb-2">{product.category}</p>
              <h3 className="font-serif text-3xl font-medium text-chocolate">{product.name}</h3>
              <p className="font-serif text-2xl text-golddark mt-2" data-testid="product-modal-price">{product.price}</p>
              <p className="text-sm text-chocolate/60 mt-4 leading-relaxed">{product.tagline}</p>
              <div className="mt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/60 mb-3">Inside the edit</p>
                <ul className="space-y-2">
                  {product.contents.map((c) => (
                    <li key={c} className="flex items-start gap-2.5 text-sm text-chocolate/80">
                      <Check size={14} className="text-gold mt-0.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8 pt-6 border-t border-chocolate/10">
                <button
                  onClick={buy}
                  data-testid="product-buy-now-button"
                  disabled={buying}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors disabled:opacity-60"
                >
                  <Lock size={13} />
                  {buying ? "Preparing secure checkout…" : `Buy now — ${product.price}`}
                </button>
                <p className="text-[11px] text-chocolate/45 leading-relaxed mt-3">
                  Secure card checkout · includes signature wrapping · {product.lead}
                </p>
              </div>
              <form onSubmit={submit} data-testid="product-enquiry-form" className="mt-6 pt-6 border-t border-chocolate/10 space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-golddark">Prefer to ask first? Send an enquiry</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="pm-name">Your name *</label>
                    <input id="pm-name" data-testid="product-enquiry-name" className={inputCls} value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Appleton" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="pm-email">Email *</label>
                    <input id="pm-email" data-testid="product-enquiry-email" type="email" className={inputCls} value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="pm-message">Message</label>
                  <textarea id="pm-message" data-testid="product-enquiry-message" rows={3} className={inputCls} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <button
                  type="submit"
                  data-testid="product-enquiry-submit"
                  disabled={sending}
                  className="w-full bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
                <p className="text-[11px] text-chocolate/45 leading-relaxed">
                  Questions welcome — we'll confirm availability and details personally before you commit to anything.
                </p>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
